import * as THREE from 'three';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import { LightSettings, LightConfig } from '../types';

export class LightManager {
  private lightRig: THREE.Group;
  private lightTarget: THREE.Object3D;
  private lights: {
    top: THREE.RectAreaLight;
    bottom: THREE.RectAreaLight;
  };
  // Дополнительные контровые (back/rim) источники света
  private rimLights: {
    left: THREE.DirectionalLight;
    right: THREE.DirectionalLight;
  };
  private lightHelpers: {
    top: THREE.Mesh;
    bottom: THREE.Mesh;
    left: THREE.Mesh;
    right: THREE.Mesh;
  };

  private readonly BASE_POSITIONS = {
    top: new THREE.Vector3(0, 3.8, -0.5),      // Верхний плоский свет
    bottom: new THREE.Vector3(0, -3.4, 0),     // Нижний плоский свет
  };

  constructor(scene: THREE.Scene) {
    // Инициализируем библиотеку для RectAreaLight (обязательно!)
    RectAreaLightUniformsLib.init();
    
    this.lightRig = new THREE.Group();
    this.lightTarget = new THREE.Object3D();
    
    scene.add(this.lightRig);
    scene.add(this.lightTarget);

    this.lights = this.createLights();
    this.rimLights = this.createRimLights();
    this.setupLights();
    this.lightHelpers = this.createLightHelpers();
    this.placeRig(1);
  }

  private createLights() {
    // Верхний свет - плоскостной свет
    const top = new THREE.RectAreaLight(0xffffff, 0.6, 4, 4);
    top.position.copy(this.BASE_POSITIONS.top);
    top.rotation.x = -Math.PI / 2;
    top.lookAt(0, 0, 0);

    // Нижний свет - плоскостной свет
    const bottom = new THREE.RectAreaLight(0xffffff, 0, 4, 4);
    bottom.position.copy(this.BASE_POSITIONS.bottom);
    bottom.rotation.x = Math.PI / 2; // Светит вверх
    bottom.lookAt(0, 0, 0);

    return { top, bottom };
  }

  /**
   * Создаём два контровых источника света (rim lights), которые не отображаются в UI/минимэп.
   * Они дают лёгкий обводящий свет сзади-сбоку, подчёркивая силуэт модели.
   */
  private createRimLights(): { left: THREE.DirectionalLight; right: THREE.DirectionalLight } {
    const left = new THREE.DirectionalLight(0xffffff, 7);
    const right = new THREE.DirectionalLight(0xffffff, 7);

    // Изначальные позиции (масштабируются в placeRig)
    left.position.set(-2.5, 1.5, -3.0);
    right.position.set(2.5, 1.5, -3.0);

    // Оба смотрят в центр сцены через общий target
    left.target = this.lightTarget;
    right.target = this.lightTarget;

    // Включаем тени для rim lights
    left.castShadow = true;
    right.castShadow = true;
    
    // Настройка качества теней
    left.shadow.mapSize.width = 2048;
    left.shadow.mapSize.height = 2048;
    left.shadow.camera.near = 0.1;
    left.shadow.camera.far = 10;
    left.shadow.camera.left = -3;
    left.shadow.camera.right = 3;
    left.shadow.camera.top = 3;
    left.shadow.camera.bottom = -3;
    left.shadow.bias = -0.0001;
    
    right.shadow.mapSize.width = 2048;
    right.shadow.mapSize.height = 2048;
    right.shadow.camera.near = 0.1;
    right.shadow.camera.far = 10;
    right.shadow.camera.left = -3;
    right.shadow.camera.right = 3;
    right.shadow.camera.top = 3;
    right.shadow.camera.bottom = -3;
    right.shadow.bias = -0.0001;

    // DirectionalLight требует, чтобы target был в сцене — он уже добавлен в конструкторе
    return { left, right };
  }

  private setupLights(): void {
    Object.values(this.lights).forEach(light => {
      this.lightRig.add(light);
    });

    // Контровые источники добавляем в тот же риг, но не отображаем в UI/минимэп
    this.lightRig.add(this.rimLights.left);
    this.lightRig.add(this.rimLights.right);

    // Минимальный ambient light - почти незаметный
    this.lightRig.add(new THREE.AmbientLight(0xffffff, 0.05));
  }

  private createLightHelpers(): { top: THREE.Mesh; bottom: THREE.Mesh; left: THREE.Mesh; right: THREE.Mesh } {
    // Создаем визуальные индикаторы для источников света
    const helperGeometry = new THREE.SphereGeometry(0.15, 16, 16);
    
    // Индикатор для верхнего света (белый/голубоватый)
    const topHelperMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x88ccff, 
      transparent: true, 
      opacity: 0.8,
      emissive: 0x88ccff,
      emissiveIntensity: 0.5
    });
    const topHelper = new THREE.Mesh(helperGeometry, topHelperMaterial);
    topHelper.position.copy(this.lights.top.position);
    // Не отображаем верхний свет в 3D-сцене, используем только для миникарты/выбора
    topHelper.visible = false;
    this.lightRig.add(topHelper);
    
    // Индикатор для нижнего света (оранжево-желтый)
    const bottomHelperMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffaa44, 
      transparent: true, 
      opacity: 0.8,
      emissive: 0xffaa44,
      emissiveIntensity: 0.5
    });
    const bottomHelper = new THREE.Mesh(helperGeometry, bottomHelperMaterial);
    bottomHelper.position.copy(this.lights.bottom.position);
    // Не отображаем нижний свет в 3D-сцене, используем только для миникарты/выбора
    bottomHelper.visible = false;
    this.lightRig.add(bottomHelper);

    // Индикатор для левого контрового света (розовый)
    const leftHelperMaterial = new THREE.MeshBasicMaterial({
      color: 0xff66cc,
      transparent: true,
      opacity: 0.9
    });
    const leftHelper = new THREE.Mesh(helperGeometry, leftHelperMaterial);
    leftHelper.position.copy(this.rimLights.left.position);
    // Не отображаем контровой свет в 3D-сцене, используем только на миникарте
    leftHelper.visible = false;
    this.lightRig.add(leftHelper);

    // Индикатор для правого контрового света (бирюзовый)
    const rightHelperMaterial = new THREE.MeshBasicMaterial({
      color: 0x66ffee,
      transparent: true,
      opacity: 0.9
    });
    const rightHelper = new THREE.Mesh(helperGeometry, rightHelperMaterial);
    rightHelper.position.copy(this.rimLights.right.position);
    // Не отображаем контровой свет в 3D-сцене, используем только на миникарте
    rightHelper.visible = false;
    this.lightRig.add(rightHelper);
    
    return { top: topHelper, bottom: bottomHelper, left: leftHelper, right: rightHelper };
  }

  // Дефолтные значения интенсивности
  private readonly DEFAULT_INTENSITIES = {
    top: 0.6,
    bottom: 0,
    left: 7,
    right: 7
  };

  // Дефолтный цвет света (белый)
  private readonly DEFAULT_COLOR = 0xffffff;
  
  // Текущий цвет света
  private currentLightColor: number = this.DEFAULT_COLOR;

  public placeRig(modelScale: number): void {
    this.lightTarget.position.set(0, 0, 0);
    
    const scale = Math.max(1, modelScale);
    
    this.lights.top.position.copy(this.BASE_POSITIONS.top).multiplyScalar(scale);
    this.lights.top.lookAt(0, 0, 0); // Плоскостной свет всегда смотрит вниз на центр
    
    this.lights.bottom.position.copy(this.BASE_POSITIONS.bottom).multiplyScalar(scale);
    this.lights.bottom.lookAt(0, 0, 0); // Плоскостной свет всегда смотрит вверх на центр

    // Расставляем контровые источники с учётом масштаба модели.
    // Левый — слева-сзади, правый — справа-сзади, немного выше центра.
    this.rimLights.left.position.set(-2.5 * scale, 1.5 * scale, -3.0 * scale);
    this.rimLights.right.position.set(2.5 * scale, 1.5 * scale, -3.0 * scale);
    this.rimLights.left.target = this.lightTarget;
    this.rimLights.right.target = this.lightTarget;

    // Обновляем позиции визуальных индикаторов
    if (this.lightHelpers) {
      this.lightHelpers.top.position.copy(this.lights.top.position);
      this.lightHelpers.bottom.position.copy(this.lights.bottom.position);
      this.lightHelpers.left.position.copy(this.rimLights.left.position);
      this.lightHelpers.right.position.copy(this.rimLights.right.position);
    }

    this.setSpotDistances(scale);
    this.updateSpotLights();
  }

  /**
   * Сброс настроек освещения к значениям по умолчанию
   */
  public resetToDefaults(modelScale: number = 1): void {
    // Сбрасываем позиции
    this.placeRig(modelScale);
    
    // Сбрасываем интенсивности
    this.lights.top.intensity = this.DEFAULT_INTENSITIES.top;
    this.lights.bottom.intensity = this.DEFAULT_INTENSITIES.bottom;
    this.rimLights.left.intensity = this.DEFAULT_INTENSITIES.left;
    this.rimLights.right.intensity = this.DEFAULT_INTENSITIES.right;
    
    // Сбрасываем цвет
    this.setAllLightsColor(this.DEFAULT_COLOR);
  }

  private setSpotDistances(scale: number): void {
    // RectAreaLight не имеет distance, но можно масштабировать размер
    const lightSize = scale * 4;
    this.lights.top.width = lightSize;
    this.lights.top.height = lightSize;
    this.lights.bottom.width = lightSize;
    this.lights.bottom.height = lightSize;
  }

  private updateSpotLights(): void {
    // Обновление spot lights (если нужно)
  }

  public getLights() {
    // Возвращаем как основные источники, так и контровые для отладочной панели
    return {
      top: this.lights.top,
      bottom: this.lights.bottom,
      left: this.rimLights.left,
      right: this.rimLights.right
    };
  }

  // Возвращаем вспомогательные меши для всех источников света (для выбора в сцене)
  public getLightHelpers(): { [name: string]: THREE.Mesh } {
    return this.lightHelpers;
  }

  // Копируем позиции хелперов обратно в реальные источники света
  public syncLightsFromHelpers(): void {
    if (!this.lightHelpers) return;
    this.lights.top.position.copy(this.lightHelpers.top.position);
    this.lights.top.lookAt(0, 0, 0);

    this.lights.bottom.position.copy(this.lightHelpers.bottom.position);
    this.lights.bottom.lookAt(0, 0, 0);

    this.rimLights.left.position.copy(this.lightHelpers.left.position);
    this.rimLights.right.position.copy(this.lightHelpers.right.position);
  }

  public getLightSettings(): any {
    return {
      color: this.currentLightColor,
      top: {
        position: { x: this.lights.top.position.x, y: this.lights.top.position.y, z: this.lights.top.position.z },
        intensity: this.lights.top.intensity,
        width: this.lights.top.width,
        height: this.lights.top.height
      },
      bottom: {
        position: { x: this.lights.bottom.position.x, y: this.lights.bottom.position.y, z: this.lights.bottom.position.z },
        intensity: this.lights.bottom.intensity,
        width: this.lights.bottom.width,
        height: this.lights.bottom.height
      },
      // Контровые источники: даём позицию и интенсивность, без width/height
      left: {
        position: { x: this.rimLights.left.position.x, y: this.rimLights.left.position.y, z: this.rimLights.left.position.z },
        intensity: this.rimLights.left.intensity
      },
      right: {
        position: { x: this.rimLights.right.position.x, y: this.rimLights.right.position.y, z: this.rimLights.right.position.z },
        intensity: this.rimLights.right.intensity
      }
    };
  }
      
  public setLightProperty(lightName: string, property: string, value: any): void {
    // Ищем свет как среди основных, так и среди контровых
    const allLights: any = {
      top: this.lights.top,
      bottom: this.lights.bottom,
      left: this.rimLights.left,
      right: this.rimLights.right
    };
    const light = allLights[lightName];
    if (!light) return;
    
    if (property.startsWith('position.')) {
      const axis = property.split('.')[1] as 'x' | 'y' | 'z';
      light.position[axis] = value;
      if (lightName === 'top' || lightName === 'bottom') {
        light.lookAt(0, 0, 0);
        // Обновляем позицию визуального индикатора
        if (this.lightHelpers && (lightName === 'top' || lightName === 'bottom')) {
          (this.lightHelpers as any)[lightName].position.copy(light.position);
        }
      }
    } else if (property === 'intensity') {
      light.intensity = value;
    } else if (property === 'angle' && light.angle !== undefined) {
      light.angle = THREE.MathUtils.degToRad(value);
    } else if (property === 'penumbra' && light.penumbra !== undefined) {
      light.penumbra = value;
    } else if (property === 'width' && light.width !== undefined) {
      light.width = value;
    } else if (property === 'height' && light.height !== undefined) {
      light.height = value;
    }
  }

  public setTopLightPosition(x: number, z: number, y?: number): void {
    const currentY = y !== undefined ? y : this.lights.top.position.y;
    const scale = 3;
    this.lights.top.position.set(x * scale, currentY, z * scale);
    // Плоскостной свет всегда смотрит вниз на центр
    this.lights.top.lookAt(0, 0, 0);
    // Обновляем позицию визуального индикатора
    if (this.lightHelpers) {
      this.lightHelpers.top.position.copy(this.lights.top.position);
    }
  }

  public setTopLightHeight(height: number): void {
    this.lights.top.position.y = height;
    // Обновляем направление после изменения высоты
    this.lights.top.lookAt(0, 0, 0);
    // Обновляем позицию визуального индикатора
    if (this.lightHelpers) {
      this.lightHelpers.top.position.copy(this.lights.top.position);
    }
  }

  public getTopLightPosition(): { x: number, y: number, z: number } {
    const scale = 3;
    return {
      x: this.lights.top.position.x / scale,
      y: this.lights.top.position.y,
      z: this.lights.top.position.z / scale
    };
      }
      
  public setBottomLightPosition(x: number, z: number, y?: number): void {
    const currentY = y !== undefined ? y : this.lights.bottom.position.y;
    const scale = 3;
    this.lights.bottom.position.set(x * scale, currentY, z * scale);
    // Плоскостной свет всегда смотрит вверх на центр
    this.lights.bottom.lookAt(0, 0, 0);
    // Обновляем позицию визуального индикатора
    if (this.lightHelpers) {
      this.lightHelpers.bottom.position.copy(this.lights.bottom.position);
    }
  }

  public setBottomLightHeight(height: number): void {
    this.lights.bottom.position.y = height;
    // Обновляем направление после изменения высоты
    this.lights.bottom.lookAt(0, 0, 0);
    // Обновляем позицию визуального индикатора
    if (this.lightHelpers) {
      this.lightHelpers.bottom.position.copy(this.lights.bottom.position);
    }
  }

  public getBottomLightPosition(): { x: number, y: number, z: number } {
    const scale = 3;
    return {
      x: this.lights.bottom.position.x / scale,
      y: this.lights.bottom.position.y,
      z: this.lights.bottom.position.z / scale
    };
  }

  // ===== Rim (left/right) helpers in "minimap space" (x/z divided by scale=3) =====
  public setRimLightPosition(side: 'left' | 'right', x: number, z: number, y?: number): void {
    const light = side === 'left' ? this.rimLights.left : this.rimLights.right;
    if (!light) return;
    const currentY = y !== undefined ? y : light.position.y;
    const scale = 3;
    light.position.set(x * scale, currentY, z * scale);
    // DirectionalLight: target already points to center via lightTarget
    if (this.lightHelpers) {
      (this.lightHelpers as any)[side]?.position?.copy(light.position);
    }
  }

  public setRimLightHeight(side: 'left' | 'right', height: number): void {
    const light = side === 'left' ? this.rimLights.left : this.rimLights.right;
    if (!light) return;
    light.position.y = height;
    if (this.lightHelpers) {
      (this.lightHelpers as any)[side]?.position?.copy(light.position);
    }
  }

  public getRimLightPosition(side: 'left' | 'right'): { x: number, y: number, z: number } {
    const light = side === 'left' ? this.rimLights.left : this.rimLights.right;
    const scale = 3;
    return {
      x: (light?.position.x ?? 0) / scale,
      y: (light?.position.y ?? 0),
      z: (light?.position.z ?? 0) / scale
    };
  }

  public setLightIntensity(lightName: string, intensity: number): void {
    if (lightName === 'top') {
      this.lights.top.intensity = intensity;
    } else if (lightName === 'bottom') {
      this.lights.bottom.intensity = intensity;
    } else if (lightName === 'left' && this.rimLights.left) {
      this.rimLights.left.intensity = intensity;
    } else if (lightName === 'right' && this.rimLights.right) {
      this.rimLights.right.intensity = intensity;
    }
  }

  /**
   * Устанавливает цвет для всех источников света одновременно
   */
  public setAllLightsColor(color: number | string): void {
    // Конвертируем строку в число если нужно
    const colorValue = typeof color === 'string' 
      ? parseInt(color.replace('#', ''), 16) 
      : color;
    
    this.currentLightColor = colorValue;
    
    // Применяем цвет ко всем источникам
    this.lights.top.color.setHex(colorValue);
    this.lights.bottom.color.setHex(colorValue);
    this.rimLights.left.color.setHex(colorValue);
    this.rimLights.right.color.setHex(colorValue);
  }

  /**
   * Возвращает текущий цвет света в виде hex строки
   */
  public getLightColor(): string {
    return '#' + this.currentLightColor.toString(16).padStart(6, '0');
  }

  /**
   * Возвращает текущий цвет света в виде числа
   */
  public getLightColorHex(): number {
    return this.currentLightColor;
  }

  public applySettings(settings: LightSettings | any): void {
    try {
      // Применяем цвет света, если он есть в настройках
      if (settings.color !== undefined) {
        this.setAllLightsColor(settings.color);
      }

      // Top light (RectAreaLight)
      if (settings.top) {
        if (settings.top.intensity !== undefined) this.lights.top.intensity = settings.top.intensity;
        if (settings.top.position) {
          const p = settings.top.position;
          this.lights.top.position.set(p.x ?? this.lights.top.position.x, p.y ?? this.lights.top.position.y, p.z ?? this.lights.top.position.z);
          this.lights.top.lookAt(0, 0, 0);
        }
        // Для RectAreaLight можно менять размер через width/height
        // angle и penumbra не применяются для RectAreaLight
      }
      
      // Bottom light (RectAreaLight)
      if (settings.bottom) {
        if (settings.bottom.intensity !== undefined) this.lights.bottom.intensity = settings.bottom.intensity;
        if (settings.bottom.width !== undefined) this.lights.bottom.width = settings.bottom.width;
        if (settings.bottom.height !== undefined) this.lights.bottom.height = settings.bottom.height;
        if (settings.bottom.position) {
          const p = settings.bottom.position;
          this.lights.bottom.position.set(p.x ?? this.lights.bottom.position.x, p.y ?? this.lights.bottom.position.y, p.z ?? this.lights.bottom.position.z);
          this.lights.bottom.lookAt(0, 0, 0);
        }
      }

      // Контровые источники (DirectionalLight)
      if (settings.left) {
        if (settings.left.intensity !== undefined) this.rimLights.left.intensity = settings.left.intensity;
        if (settings.left.position) {
          const p = settings.left.position;
          this.rimLights.left.position.set(p.x ?? this.rimLights.left.position.x, p.y ?? this.rimLights.left.position.y, p.z ?? this.rimLights.left.position.z);
        }
      }

      if (settings.right) {
        if (settings.right.intensity !== undefined) this.rimLights.right.intensity = settings.right.intensity;
        if (settings.right.position) {
          const p = settings.right.position;
          this.rimLights.right.position.set(p.x ?? this.rimLights.right.position.x, p.y ?? this.rimLights.right.position.y, p.z ?? this.rimLights.right.position.z);
        }
      }

      // Обновляем позиции визуальных индикаторов (helpers) после применения настроек
      if (this.lightHelpers) {
        this.lightHelpers.top.position.copy(this.lights.top.position);
        this.lightHelpers.bottom.position.copy(this.lights.bottom.position);
        this.lightHelpers.left.position.copy(this.rimLights.left.position);
        this.lightHelpers.right.position.copy(this.rimLights.right.position);
      }
      
      console.log('✅ Световые настройки применены');
    } catch (error) {
      console.error('Ошибка применения световых настроек:', error);
    }
  }

  public exportSettings(): any {
    // Экспортируем полные настройки всех источников света
    return this.getLightSettings();
  }
}
